import React, { Component } from 'react';
import PropTypes from 'prop-types';
import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';
import { hateoas } from '../../lib/links';
import { request } from '../../lib/Actions';
import { FETCH } from '../../entities/PaginatedItems/actions';
import * as selectors from '../../entities/PaginatedItems/selectors';

/**
 * Higher order component to add pagination
 * Passes ids of underlying entities to wrapped component
 * @param WrappedComponent
 * @param mapStateToProps additional props to bind into component (for determining
 *   refresh, for example)
 * @param shouldRefresh takes arguments prevProps, nextProps and should return a bool if a refresh
 *   should be triggered
 * @returns {*}
 */
const withPagination = (
  mapStateToProps = undefined,
  shouldRefresh = undefined,
  useInfinitePagination = false,
) => (WrappedComponent) => {
  function paginationMapStateToProps(state, ownProps) {
    const pagination = selectors.paginationByUrl(state, ownProps.url).toJS();
    const additionalProps = mapStateToProps ? mapStateToProps(state, ownProps) : ({});
    return {
      ...pagination,
      ids: (pagination.items || []).map(item => item.id),
      workPreference: pagination.workPreference || null,
      ...additionalProps,
    };
  }

  function paginationMapDispatchToProps(dispatch, { url }) {
    return bindActionCreators({
      fetch: ({ fetchUrl, isInfinitePagination = false }) => request(FETCH)({ url, fetchUrl, isInfinitePagination }),
    }, dispatch);
  }

  class PaginatedComponent extends Component {
    UNSAFE_componentWillMount() {
      this.fetchData();
    }

    UNSAFE_componentWillReceiveProps(nextProps) {
      if (shouldRefresh !== undefined && shouldRefresh(this.props, nextProps)) {
        this.refresh();
      }

      if (this.props.url !== nextProps.url || this.props.fetchUrl !== nextProps.fetchUrl) {
        nextProps.fetch({
          fetchUrl: nextProps.fetchUrl,
          isInfinitePagination: useInfinitePagination,
        });
      }
    }

    refresh = () => this.props.fetch({
      url: this.props.url,
      fetchUrl: this.props.fetchUrl,
      isInfinitePagination: false,
    });

    hasPrevPage = () => hateoas(this.props.links).get('prev');

    hasNextPage = () => hateoas(this.props.links).get('next');

    prevPage = () => this.props.fetch({
      fetchUrl: hateoas(this.props.links).get('prev'),
      isInfinitePagination: false,
    });

    nextPage = () => this.props.fetch({
      fetchUrl: hateoas(this.props.links).get('next'),
      isInfinitePagination: useInfinitePagination,
    });

    fetchData = () => {
      const {
        fetch, fetchUrl, url, limit,
      } = this.props;
      if (fetchUrl) {
        fetch({
          fetchUrl,
        });
      } else if (url) {
        fetch({
          fetchUrl: limit ? `${url}?limit=${limit}` : undefined,
        });
      }
    }

    render() {
      return (
        <WrappedComponent
          {...this.props}
          isLoading={this.props.isLoading}
          ids={this.props.ids}
          refresh={this.refresh}
          prevPage={undefined}
          nextPage={this.hasNextPage() ? this.nextPage : undefined}
          hasItems={this.props.itemCount > 0}
          fetchData={this.fetchData}
        />
      );
    }
  }

  PaginatedComponent.defaultProps = {
    ids: [],
    itemCount: 0,
    items: [],
    itemsPerPage: 0,
    links: [],
    pageCount: 0,
    isLoading: false,
    limit: undefined,
    fetchUrl: undefined,
    url: undefined,
    hasItems: false,
  };

  PaginatedComponent.propTypes = {
    ids: PropTypes.arrayOf(PropTypes.oneOfType([PropTypes.number, PropTypes.string])),
    itemCount: PropTypes.number,
    itemsPerPage: PropTypes.number,
    limit: PropTypes.number,
    links: PropTypes.arrayOf(
      PropTypes.shape({
        rel: PropTypes.string.isRequired,
        href: PropTypes.string.isRequired,
      }),
    ),
    pageCount: PropTypes.number,
    url: PropTypes.string,
    fetch: PropTypes.func.isRequired,
    fetchUrl: PropTypes.string,
    isLoading: PropTypes.bool,
    hasItems: PropTypes.bool,
  };

  return connect(paginationMapStateToProps, paginationMapDispatchToProps)(PaginatedComponent);
};

export default withPagination;
